// frontend/src/components/Transferform.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftRight, Save, X, Loader2, AlertCircle,
  Building, Calendar, FileText, DollarSign, RefreshCw,
  CheckCircle, Send, Layers, TrendingUp, TrendingDown,
  CreditCard, Landmark, Smartphone, Wallet
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Transferform.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const Transferform = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [fromAccounts, setFromAccounts] = useState([]);
  const [toAccounts, setToAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // For system admins: company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    fromaccount_id: "",
    toaccount_id: "",
    amount: "",
    description: "",
    transaction_date: new Date().toISOString().split('T')[0],
    company_id: ""
  });

  // Calculate user permissions
  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };

  const isCompanyUser = () => {
    return userType === 'company';
  };

  const userCanCreateTransfer = () => {
    return isCompanyUser() || isSystemAdminUser();
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  // Fetch companies for system admins
  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Fetch accounts based on selected company
  const fetchAccounts = async (companyId) => {
    if (!companyId) return;

    try {
      setLoadingAccounts(true);

      let endpoint = `${url}/api/account/dropdown?include_inactive=false&type=Asset&posting_only=true&transfer_only=true`;
      if (companyId) {
        endpoint += `&company_id=${companyId}`;
      }

      const response = await axios.get(endpoint);

      if (response.data.success) {
        const accountsData = response.data.data || [];
        setAllAccounts(accountsData);
        setFromAccounts(accountsData);
        setToAccounts(accountsData);
      } else {
        toast.error(response.data.message || "Failed to load accounts");
      }
    } catch (error) {
      toast.error("Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Filter to accounts when from account is selected
  useEffect(() => {
    if (formData.fromaccount_id && allAccounts.length > 0) {
      const filtered = allAccounts.filter(acc => acc._id !== formData.fromaccount_id);
      setToAccounts(filtered);
    } else {
      setToAccounts(allAccounts);
    }
  }, [formData.fromaccount_id, allAccounts]);

  // Filter from accounts when to account is selected
  useEffect(() => {
    if (formData.toaccount_id && allAccounts.length > 0) {
      const filtered = allAccounts.filter(acc => acc._id !== formData.toaccount_id);
      setFromAccounts(filtered);
    } else {
      setFromAccounts(allAccounts);
    }
  }, [formData.toaccount_id, allAccounts]);

  // Check permissions and setup initial data
  useEffect(() => {
    const checkPermissionsAndSetup = async () => {
      try {
        if (!userCanCreateTransfer()) {
          toast.error("You don't have permission to create transfers");
          navigate("/");
          return;
        }
        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({
            ...prev,
            company_id: companyId
          }));
          await fetchAccounts(companyId);
        }
        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };

    checkPermissionsAndSetup();
  }, []);

  // Fetch accounts when company changes for system admin
  useEffect(() => {
    if (isSystemAdminUser() && selectedCompany) {
      fetchAccounts(selectedCompany);
      setFormData(prev => ({ ...prev, company_id: selectedCompany }));
    }
  }, [selectedCompany]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCompanyChange = async (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({
      ...prev,
      company_id: value,
      fromaccount_id: "",
      toaccount_id: ""
    }));
  };
  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => {
    setFocused({
      ...focused,
      [field]: formData[field] && formData[field].toString().length > 0
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fromaccount_id) {
      newErrors.fromaccount_id = "From account is required";
    }

    if (!formData.toaccount_id) {
      newErrors.toaccount_id = "To account is required";
    }

    if (formData.fromaccount_id && formData.toaccount_id &&
      formData.fromaccount_id === formData.toaccount_id) {
      newErrors.toaccount_id = "From and To accounts cannot be the same";
    }

    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    if (!formData.transaction_date) {
      newErrors.transaction_date = "Transaction date is required";
    }

    if (!formData.company_id) {
      newErrors.company_id = "Company selection is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }
  
    if (!formData.fromaccount_id || !formData.toaccount_id) {
      toast.error("Please select valid accounts");
      return;
    }
  
    const selectedFromAccount = allAccounts.find(acc => acc._id === formData.fromaccount_id);
    const selectedToAccount = allAccounts.find(acc => acc._id === formData.toaccount_id);
  
    if (!selectedFromAccount || !selectedToAccount) {
      toast.error("Account not found – refresh and try again");
      return;
    }
  
    if (selectedFromAccount.company_id !== formData.company_id) {
      toast.error(`From account doesn't belong to current company`);
      return;
    }
    if (selectedToAccount.company_id !== formData.company_id) {
      toast.error(`To account doesn't belong to current company`);
      return;
    }
  
    const amount = parseFloat(formData.amount);
    if (selectedFromAccount.balance < amount) {
      toast.error(`Insufficient balance. Available: ${formatAmount(selectedFromAccount.balance)}`);
      return;
    }
  
    try {
      setSubmitting(true);
  
      // ✅ Correct field names as expected by the backend
      const payload = {
        fromaccount_id: formData.fromaccount_id,
        toaccount_id: formData.toaccount_id,
        amount: amount,
        transaction_date: formData.transaction_date,
        company_id: formData.company_id,
        transfer_type: "OUT",
        description: formData.description || "",
      };
      
      const response = await axios.post(`${url}/api/transferform`, payload);
  
      if (response.data.success) {
        toast.success("Transfer created successfully!");
        // Reset form
        setFormData({
          fromaccount_id: "",
          toaccount_id: "",
          amount: "",
          description: "",
          transaction_date: new Date().toISOString().split('T')[0],
          company_id: isSystemAdminUser() ? "" : company?._id || ""
        });
        setSelectedCompany("");
        setTimeout(() => navigate("/ViewTransfers"), 1000);
      } else {
        toast.error(response.data.message || "Failed to create transfer");
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data?.message || "Server error");
      } else {
        toast.error("Network error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewTransfers");
  };

  // Get selected account for balance display
  const selectedFromAccount = allAccounts.find(acc => acc._id === formData.fromaccount_id);
  const selectedToAccount = allAccounts.find(acc => acc._id === formData.toaccount_id);

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="transfer-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <ArrowLeftRight size={24} />
          <h2 className="form-title">Create New Transfer</h2>
          <p className="form-subtitle">
            Transfer money between accounts
          </p>
        </div>

        <div className="role-indicator">
          <Building size={20} />
          <div>
            <strong>
              {isSystemAdminUser() ? "System Administrator" : "Company User"}
            </strong>
            <div className="role-subtitle">
              {isSystemAdminUser()
                ? "You can create transfers for any company"
                : `Creating transfer for ${company?.company_name || 'your company'}`
              }
            </div>
          </div>
        </div>

        <form className="transfer-form" onSubmit={handleSubmit}>
          {/* Company Selection (System Admins Only) */}
          {isSystemAdminUser() && (
            <div className={`form-field required ${focused.company_id || formData.company_id ? "filled" : ""} ${errors.company_id ? 'error' : ''}`}>
              <div className="select-wrapper">
                <select
                  name="company_id"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  onFocus={() => handleFocus("company_id")}
                  onBlur={() => handleBlur("company_id")}
                  required
                  disabled={loadingCompanies}
                >
                  <option value="">Select Company</option>
                  {companies.map((comp) => (
                    <option key={comp._id} value={comp._id}>
                      {comp.company_name} ({comp.company_code})
                    </option>
                  ))}
                </select>
                <Building size={16} className="select-icon" />
              </div>
              <label>Company</label>
              {errors.company_id && <small className="field-error">{errors.company_id}</small>}
              {loadingCompanies && <small className="field-hint">Loading companies...</small>}
            </div>
          )}

          {/* From Account */}
          <div className={`form-field required ${focused.fromaccount_id || formData.fromaccount_id ? "filled" : ""} ${errors.fromaccount_id ? 'error' : ''}`}>
            <div className="select-wrapper">
              <select
                name="fromaccount_id"
                value={formData.fromaccount_id}
                onChange={handleChange}
                onFocus={() => handleFocus("fromaccount_id")}
                onBlur={() => handleBlur("fromaccount_id")}
                required
                disabled={loadingAccounts || !formData.company_id}
              >
                <option value="">Select From Account</option>

                {fromAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.account_code} — {account.account_name} (
                    {account.accountingType?.sub_type || account.type}
                    ) - Balance: {formatAmount(account.balance)}
                  </option>
                ))}
              </select>
              <TrendingDown size={16} className="select-icon" />
            </div>
            <label>From Account *</label>
            {errors.fromaccount_id && <small className="field-error">{errors.fromaccount_id}</small>}
            {!formData.company_id && <small className="field-hint">Select a company first</small>}
          </div>

          {/* Balance display for from account */}
          {selectedFromAccount && (
            <div className="balance-info" style={{
              marginTop: '-10px',
              marginBottom: '10px',
              padding: '8px 12px',
              background: '#e6f7ff',
              borderRadius: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <DollarSign size={14} color="#1890ff" />
              <span>Available Balance: <strong>{formatAmount(selectedFromAccount.balance)}</strong></span>
              {selectedFromAccount.balance < parseFloat(formData.amount || 0) && formData.amount && (
                <span style={{ color: '#ff4d4f', marginLeft: 'auto' }}>
                  ⚠️ Insufficient balance!
                </span>
              )}
            </div>
          )}

          {/* To Account */}
          <div className={`form-field required ${focused.toaccount_id || formData.toaccount_id ? "filled" : ""} ${errors.toaccount_id ? 'error' : ''}`}>
            <div className="select-wrapper">
              <select
                name="toaccount_id"
                value={formData.toaccount_id}
                onChange={handleChange}
                onFocus={() => handleFocus("toaccount_id")}
                onBlur={() => handleBlur("toaccount_id")}
                required
                disabled={loadingAccounts || !formData.company_id}
              >
                <option value="">Select To Account</option>
                {toAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.account_code} — {account.account_name} ({account.accountingType?.sub_type || account.type}) - Balance: {formatAmount(account.balance)}
                  </option>
                ))}
              </select>
              <TrendingUp size={16} className="select-icon" />
            </div>
            <label>To Account *</label>
            {errors.toaccount_id && <small className="field-error">{errors.toaccount_id}</small>}
          </div>

          {/* Amount */}
          <div className={`form-field required ${focused.amount || formData.amount ? "filled" : ""} ${errors.amount ? 'error' : ''}`}>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              onFocus={() => handleFocus("amount")}
              onBlur={() => handleBlur("amount")}
              placeholder="Enter amount"
              min="0.01"
              step="0.01"
              required
            />
            <label>
              <DollarSign size={14} className="mr-2" />
              Amount *
            </label>
            {errors.amount && <small className="field-error">{errors.amount}</small>}
          </div>

          {/* Transaction Date */}
          <div className={`form-field required ${focused.transaction_date || formData.transaction_date ? "filled" : ""} ${errors.transaction_date ? 'error' : ''}`}>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleChange}
              onFocus={() => handleFocus("transaction_date")}
              onBlur={() => handleBlur("transaction_date")}
              required
            />
            <label>
              <Calendar size={14} className="mr-2" />
              Transaction Date *
            </label>
            {errors.transaction_date && <small className="field-error">{errors.transaction_date}</small>}
          </div>

          {/* Description */}
          <div className={`form-field ${focused.description || formData.description ? "filled" : ""} ${errors.description ? 'error' : ''}`}>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onFocus={() => handleFocus("description")}
              onBlur={() => handleBlur("description")}
              placeholder="Enter description (optional)"
              rows="3"
            />
            <label>
              <FileText size={14} className="mr-2" />
              Description
            </label>
            {errors.description && <small className="field-error">{errors.description}</small>}
          </div>

          {/* Transfer Summary */}
          {selectedFromAccount && selectedToAccount && formData.amount && parseFloat(formData.amount) > 0 && (
            <div className="transfer-summary" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '15px 20px',
              borderRadius: '10px',
              marginTop: '10px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 600 }}>Transfer Summary</h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>From:</span>
                <strong>{selectedFromAccount.account_name}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>To:</span>
                <strong>{selectedToAccount.account_name}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Amount:</span>
                <strong>{formatAmount(parseFloat(formData.amount))}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <span>From Balance After:</span>
                <strong>{formatAmount((selectedFromAccount.balance || 0) - parseFloat(formData.amount))}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>To Balance After:</span>
                <strong>{formatAmount((selectedToAccount.balance || 0) + parseFloat(formData.amount))}</strong>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || loadingAccounts || (selectedFromAccount && selectedFromAccount.balance < parseFloat(formData.amount || 0))}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  Create Transfer
                  <Send className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Transferform;